import React, { forwardRef, useState, useCallback, useEffect, useRef, useMemo } from "react";
import { useDroppable } from "@dnd-kit/core";
import { FaTrash } from "react-icons/fa";
import { Box } from "../ui/Box";
import { DesignElement } from "./types";
import { productCustomizationApi } from "../../services/productCustomizationApi";
import { ensureFontsLoaded } from "../../utils/fontLoader";

export interface DesignCanvasProps {
 baseImage?: string;
 backgroundColor?: string;
 elements: DesignElement[];
 selectedElement: string | null;
 selectedCanvas?: boolean;
 onElementSelect: (id: string | null) => void;
 onCanvasSelect?: () => void;
 onElementUpdate: (id: string, updates: Partial<DesignElement>) => void;
 onElementDelete: (id: string) => void;
  canvasSize?: string;
  isPreviewMode?: boolean;
  onElementDoubleClick?: (id: string) => void; // Callback for double-click on elements
  isAdmin?: boolean; // Whether the user is an admin
  onRemoveWatermark?: () => void; // Callback to remove watermark (admin only)
  hasWatermark?: boolean; // Whether the current background image has a watermark
}

const DesignCanvasComponent = forwardRef<HTMLDivElement, DesignCanvasProps>(
  (
    {
     baseImage,
     backgroundColor = "#FFFFFF",
     elements,
     selectedElement,
     selectedCanvas = false,
     onElementSelect,
     onCanvasSelect,
     onElementUpdate,
     onElementDelete: _onElementDelete,
     canvasSize = "mug",
     onElementDoubleClick,
     isPreviewMode = false,
     isAdmin = false,
     onRemoveWatermark,
     hasWatermark = false,
    },
    ref
  ) => {
  // Canvas size presets (used as fallback when no background image)
  const canvasSizes: Record<string, { width: number; height: number }> = {
    mug: { width: 800, height: 600 },
    bottle: { width: 600, height: 800 },
    tshirt: { width: 1000, height: 1200 },
    poster: { width: 1200, height: 1800 },
  };

  // If we have a background image, use its actual dimensions instead of preset
  const [imageDimensions, setImageDimensions] = useState<{ width: number; height: number } | null>(null);
  
  // Always use preset canvas size - images will be scaled to fit within it
  const size = canvasSizes[canvasSize] || canvasSizes.mug;
  
  const wrapperRef = useRef<HTMLDivElement>(null);
  const backgroundCanvasRef = useRef<HTMLCanvasElement>(null);
  const lastProcessedBaseImageRef = useRef<string | undefined>(undefined);
  
  // Track wrapper dimensions for canvas sizing
  const [wrapperDimensions, setWrapperDimensions] = useState<{ width: number; height: number } | null>(null);
  
  // Measure wrapper dimensions when component mounts or resizes
  useEffect(() => {
    if (!wrapperRef.current) return;
    
    const updateDimensions = () => {
      if (wrapperRef.current) {
        const rect = wrapperRef.current.getBoundingClientRect();
        setWrapperDimensions({ width: rect.width, height: rect.height });
      }
    };
    
    updateDimensions();
    
    const resizeObserver = new ResizeObserver(updateDimensions);
    resizeObserver.observe(wrapperRef.current);
    
    return () => {
      resizeObserver.disconnect();
    };
  }, []);
  
  // Calculate canvas size: full width of parent, height maintains aspect ratio
  // Use useMemo to calculate early so it's available for callbacks
  const baseDisplayScale = useMemo(() => {
    const parentWidth = wrapperDimensions?.width || (typeof window !== "undefined" ? window.innerWidth - 600 : 1200);
    const scaleX = parentWidth / size.width;
    return Math.min(scaleX, 1); // Don't scale beyond 100%
  }, [wrapperDimensions, size.width]);
  
  const baseDisplayWidth = size.width * baseDisplayScale;
  const baseDisplayHeight = size.height * baseDisplayScale;
  const [isDragging, setIsDragging] = useState(false);
  
  // Calculate initial zoom to fit canvas in wrapper (starting at full width)
  const calculateInitialZoom = useCallback((): number => {
    // Canvas already fits full width, so start at zoom level 1
    return 1;
  }, []);
  
  const [zoomLevel, setZoomLevel] = useState<number>(() => {
    // Initial zoom will be calculated after wrapper ref is available
    return 1;
  });
  
  // Load image dimensions when baseImage changes
  useEffect(() => {
    if (baseImage) {
      const img = new window.Image();
      img.onload = () => {
        const dims = { width: img.naturalWidth, height: img.naturalHeight };
        setImageDimensions(dims);
      };
      img.onerror = () => {
        setImageDimensions(null);
      };
      img.src = baseImage;
    } else {
      setImageDimensions(null);
    }
  }, [baseImage, canvasSize]);
  
  // Calculate and set initial zoom when image dimensions are loaded
  useEffect(() => {
    const baseImageChanged = baseImage !== lastProcessedBaseImageRef.current;
    // Reset lastProcessedBaseImage when baseImage is cleared (becomes undefined/null)
    if (!baseImage) {
      lastProcessedBaseImageRef.current = undefined;
    }
    // Recalculate zoom when baseImage changed (user selected/cleared a background)
    if (wrapperRef.current && baseImageChanged) {
      lastProcessedBaseImageRef.current = baseImage;
      const initialZoom = calculateInitialZoom();
      // Always update zoom to ensure re-render when background changes
      // Directly set the zoom value - React will re-render if it's different
      setZoomLevel(initialZoom);
    }
  }, [baseImage, calculateInitialZoom]);
  
  // Handle zoom with wheel events on the wrapper container
  useEffect(() => {
    const wrapperElement = wrapperRef.current;
    const canvasElement = typeof ref === "function" || !ref ? null : ref.current;
    if (!wrapperElement || !canvasElement) return;
    
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      
      // Calculate zoom change
      const zoomSpeed = 0.1;
      const zoomDelta = e.deltaY > 0 ? -zoomSpeed : zoomSpeed;
      // Prevent zooming out beyond actual size (1 = 100%)
      // Allow zooming in up to 300% and zooming out back to 100%
      const newZoom = Math.max(1, Math.min(3, zoomLevel + zoomDelta));
      
      // Update zoom without panning - canvas stays centered
      setZoomLevel(newZoom);
    };
    
    // Use capture phase to catch events before they bubble to parent
    wrapperElement.addEventListener("wheel", handleWheel, { passive: false, capture: true });
    // Also add to bubble phase as backup
    wrapperElement.addEventListener("wheel", handleWheel, { passive: false });
    
    return () => {
      wrapperElement.removeEventListener("wheel", handleWheel, { capture: true } as any);
      wrapperElement.removeEventListener("wheel", handleWheel);
    };
  }, [ref, zoomLevel]);
  const [dragStart, setDragStart] = useState<{ x: number; y: number; elementX: number; elementY: number } | null>(null);
  const [draggedElement, setDraggedElement] = useState<string | null>(null);
  const [resizing, setResizing] = useState<{ elementId: string; corner: string } | null>(null);
  const [resizeStart, setResizeStart] = useState<{ x: number; y: number; width: number; height: number } | null>(null);

  const handleMouseDown = useCallback(
   (e: React.MouseEvent, elementId: string) => {
    if (e.button !== 0) return; // Only left click
    
    // Prevent default drag behavior for images, but allow event to bubble
    const target = e.target as HTMLElement;
    if (target.tagName === "IMG") {
      e.preventDefault();
      // Don't stop propagation - we want the event to reach the parent div
    }

    const canvasElement = typeof ref === "function" || !ref ? null : ref.current;
    const canvasRect = canvasElement?.getBoundingClientRect();
    if (!canvasRect) return;

    // Convert display coordinates to logical coordinates
    // Account for zoom (no panning - canvas is locked)
    // coordScale converts display pixels to logical canvas coordinates
    const coordScale = baseDisplayScale;
    // Account for zoom: reverse the transform
    const x = ((e.clientX - canvasRect.left) / zoomLevel) / coordScale;
    const y = ((e.clientY - canvasRect.top) / zoomLevel) / coordScale;

    // Get the initial element position
    const element = elements.find((el) => el.id === elementId);
    if (!element) return;

    setIsDragging(true);
    setDragStart({ x, y, elementX: element.position.x, elementY: element.position.y });
    setDraggedElement(elementId);
    onElementSelect(elementId);
   },
   [ref, onElementSelect, baseDisplayScale, zoomLevel]
  );


  const handleDeleteClick = useCallback(
   (e: React.MouseEvent, elementId: string) => {
    e.preventDefault();
    e.stopPropagation();
    _onElementDelete(elementId);
   },
   [_onElementDelete]
  );

  const handleCanvasClick = useCallback(
   (e: React.MouseEvent) => {
      // Don't select canvas if clicking on an element or delete button
      if ((e.target as HTMLElement).closest(".element-container") || 
          (e.target as HTMLElement).closest(".delete-icon-button")) {
        return;
      }
    if (onCanvasSelect) {
      onCanvasSelect();
    }
   },
   [onCanvasSelect]
  );

  const handleMouseUp = useCallback(() => {
   setIsDragging(false);
   setDragStart(null);
   setDraggedElement(null);
  }, []);

  const handleResizeStart = useCallback(
   (e: React.MouseEvent, elementId: string, corner: string) => {
    e.stopPropagation();
    const element = elements.find((el) => el.id === elementId);
    if (element) {
     setResizing({ elementId, corner });
     setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: element.size.width,
      height: element.size.height,
     });
    }
   },
   [elements]
  );

  const handleResizeEnd = useCallback(() => {
   setResizing(null);
   setResizeStart(null);
  }, []);


  React.useEffect(() => {
   if (isDragging && dragStart && draggedElement) {
    const handleMouseMove = (e: MouseEvent) => {
     if (resizing) return; // Don't handle drag if resizing
     
     const canvasElement = typeof ref === "function" || !ref ? null : ref.current;
     const canvasRect = canvasElement?.getBoundingClientRect();
     if (!canvasRect) return;

     // Convert display coordinates to logical coordinates
     // Account for zoom (no panning - canvas is locked)
     // coordScale converts display pixels to logical canvas coordinates
     const coordScale = baseDisplayScale;
     // Account for zoom: reverse the transform
     const x = ((e.clientX - canvasRect.left) / zoomLevel) / coordScale;
     const y = ((e.clientY - canvasRect.top) / zoomLevel) / coordScale;

     // Calculate absolute position based on initial element position and mouse movement
     const deltaX = x - dragStart.x;
     const deltaY = y - dragStart.y;
     const newX = Math.max(0, dragStart.elementX + deltaX);
     const newY = Math.max(0, dragStart.elementY + deltaY);

     onElementUpdate(draggedElement, {
      position: {
       x: newX,
       y: newY,
      },
     });
    };

    const handleMouseUpGlobal = () => {
     setIsDragging(false);
     setDragStart(null);
     setDraggedElement(null);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUpGlobal);

    return () => {
     window.removeEventListener("mousemove", handleMouseMove);
     window.removeEventListener("mouseup", handleMouseUpGlobal);
    };
   }
   return undefined;
  }, [isDragging, dragStart, draggedElement, elements, ref, onElementUpdate, resizing, baseDisplayScale, zoomLevel]);

  React.useEffect(() => {
   if (resizing && resizeStart) {
    const handleMouseMove = (e: MouseEvent) => {
     const canvasElement = typeof ref === "function" || !ref ? null : ref.current;
     const canvasRect = canvasElement?.getBoundingClientRect();
     if (!canvasRect) return;
     
     // Convert display coordinates to logical coordinates
     // Account for zoom (no panning - canvas is locked)
     // coordScale converts display pixels to logical canvas coordinates
     const coordScale = baseDisplayScale;
     // Account for zoom: reverse the transform for deltas
     const deltaX = ((e.clientX - resizeStart.x) / zoomLevel) / coordScale;
     const deltaY = ((e.clientY - resizeStart.y) / zoomLevel) / coordScale;

     const element = elements.find((el) => el.id === resizing.elementId);
     if (element) {
      let newWidth = resizeStart.width;
      let newHeight = resizeStart.height;

      if (resizing.corner.includes("e")) newWidth = Math.max(20, resizeStart.width + deltaX);
      if (resizing.corner.includes("w")) newWidth = Math.max(20, resizeStart.width - deltaX);
      if (resizing.corner.includes("s")) newHeight = Math.max(20, resizeStart.height + deltaY);
      if (resizing.corner.includes("n")) newHeight = Math.max(20, resizeStart.height - deltaY);

      onElementUpdate(resizing.elementId, {
       size: { width: newWidth, height: newHeight },
      });
     }
    };

    const handleMouseUp = () => {
     handleResizeEnd();
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
     window.removeEventListener("mousemove", handleMouseMove);
     window.removeEventListener("mouseup", handleMouseUp);
    };
   }
   return undefined;
  }, [resizing, resizeStart, elements, onElementUpdate, handleResizeEnd, ref, baseDisplayScale, zoomLevel]);

  const renderElement = (element: DesignElement) => {
   const isSelected = element.id === selectedElement;
   const rotation = element.style.rotation || 0;
   const opacity = element.style.opacity ?? 1;
   // Elements are positioned relative to the preset canvas size
   // Use baseDisplayScale to scale elements to match the displayed canvas size
   // (baseDisplayScale scales preset size to fit parent width)
   const elementScale = baseDisplayScale;
   
   const style: React.CSSProperties = {
    position: "absolute",
    left: `${element.position.x * elementScale}px`,
    top: `${element.position.y * elementScale}px`,
    width: `${element.size.width * elementScale}px`,
    height: `${element.size.height * elementScale}px`,
    zIndex: element.zIndex,
    cursor: "move",
    border: isSelected ? "2px dashed #3b82f6" : "none",
    transform: `rotate(${rotation}deg)`,
    transformOrigin: "center center",
    opacity: opacity,
   };

   // Apply inverse transform to controls to keep them at fixed size regardless of zoom
   const inverseTransform = `scale(${1 / zoomLevel})`;
   
   const ResizeHandle = ({ corner, className }: { corner: string; className?: string }) => (
    <div
     className={`absolute w-3 h-3 bg-blue-500 border border-white cursor-${corner}-resize ${className || ""}`}
     style={{
      [corner.includes("n") ? "top" : "bottom"]: "-6px",
      [corner.includes("e") ? "right" : "left"]: "-6px",
      transform: inverseTransform,
      transformOrigin: "center center",
     }}
     onMouseDown={(e) => handleResizeStart(e, element.id, corner)}
    />
   );

   const RotationHandle = () => (
    <div
     className="absolute -top-8 left-1/2 w-3 h-3 bg-blue-500 border border-white rounded-full cursor-grab"
     style={{
      transform: `translate(-50%, 0) ${inverseTransform}`,
      transformOrigin: "center center",
     }}
     onMouseDown={(e) => {
      e.stopPropagation();
      const startY = e.clientY;
      const startRotation = rotation;
      const handleMouseMove = (moveEvent: MouseEvent) => {
       const deltaY = moveEvent.clientY - startY;
       const newRotation = startRotation + deltaY * 0.5;
       onElementUpdate(element.id, {
        style: { ...element.style, rotation: newRotation },
       });
      };
      const handleMouseUp = () => {
       window.removeEventListener("mousemove", handleMouseMove);
       window.removeEventListener("mouseup", handleMouseUp);
      };
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
     }}
    />
   );
   
   const DeleteButton = () => (
    <button
     className="delete-icon-button absolute -bottom-8 right-0 bg-red-500 text-white p-1 rounded hover:bg-red-600 z-50"
     style={{
      transform: inverseTransform,
      transformOrigin: "bottom right",
     }}
     onClick={(e) => handleDeleteClick(e, element.id)}
     title="Delete element"
    >
     <FaTrash className="w-3 h-3" />
    </button>
   );

   switch (element.type) {
    case "text":
     return (
      <div
       key={element.id}
       className="element-container"
       data-element-id={element.id}
       style={{
         ...style,
         fontFamily: element.style.fontFamily || "Arial",
         fontSize: `${element.style.fontSize || 24}px`,
         fontWeight: element.style.fontWeight || "normal",
         fontStyle: element.style.fontStyle || "normal",
         textDecoration: element.style.textDecoration || "none",
         textAlign: (element.style.textAlign as "left" | "center" | "right" | "justify") || "left",
         lineHeight: element.style.lineHeight || 1.5,
         letterSpacing: element.style.letterSpacing || "0px",
         textTransform: (element.style.textTransform as "none" | "uppercase" | "lowercase" | "capitalize") || "none",
         color: element.style.color || "#000000",
         whiteSpace: "pre-wrap",
         wordWrap: "break-word",
       }}
       onMouseDown={!isPreviewMode ? (e) => handleMouseDown(e, element.id) : undefined}
       onDoubleClick={!isPreviewMode && onElementDoubleClick ? (e) => {
         e.stopPropagation();
         onElementDoubleClick(element.id);
       } : undefined}
      >
       {element.content || "Text"}
       {isSelected && !isPreviewMode && (
        <>
         <ResizeHandle corner="nw" />
         <ResizeHandle corner="ne" />
         <ResizeHandle corner="sw" />
         <ResizeHandle corner="se" />
         <RotationHandle />
         <DeleteButton />
        </>
       )}
      </div>
     );

    case "shape":
     return (
      <div
       key={element.id}
       className="element-container"
       data-element-id={element.id}
       style={{
        ...style,
        backgroundColor: element.content ? "transparent" : (element.style.backgroundColor || "#000000"),
        borderRadius: `${element.style.borderRadius || 0}px`,
        border: element.style.borderWidth && element.style.borderColor 
          ? `${element.style.borderWidth}px solid ${element.style.borderColor}` 
          : (isSelected ? "2px dashed #3b82f6" : "none"),
      }}
       onMouseDown={!isPreviewMode ? (e) => handleMouseDown(e, element.id) : undefined}
      >
       {element.content ? (
        <img
         src={element.content}
         alt="Shape"
         className="w-full h-full object-contain select-none"
          style={{ 
            width: "100%", 
            height: "100%", 
            userSelect: "none",
            pointerEvents: "auto",
            cursor: "move",
            ...({ WebkitUserDrag: "none" } as React.CSSProperties)
          }}
          draggable={false}
          onContextMenu={(e) => e.preventDefault()}
          onDragStart={(e) => {
            e.preventDefault();
            e.stopPropagation();
            return false;
          }}
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = "none";
          }}
        />
       ) : null}
       {isSelected && !isPreviewMode && (
        <>
         <ResizeHandle corner="nw" />
         <ResizeHandle corner="ne" />
         <ResizeHandle corner="sw" />
         <ResizeHandle corner="se" />
         <RotationHandle />
         <DeleteButton />
        </>
       )}
      </div>
     );

    case "frame":
     return (
      <div
       key={element.id}
       className="element-container"
       data-element-id={element.id}
       style={{
        ...style,
        border: `${element.style.borderWidth || 2}px solid ${element.style.borderColor || "#000000"}`,
        borderRadius: `${element.style.borderRadius || 0}px`,
        backgroundColor: "transparent",
       }}
       onMouseDown={!isPreviewMode ? (e) => handleMouseDown(e, element.id) : undefined}
      >
       {isSelected && !isPreviewMode && (
        <>
         <ResizeHandle corner="nw" />
         <ResizeHandle corner="ne" />
         <ResizeHandle corner="sw" />
         <ResizeHandle corner="se" />
         <RotationHandle />
         <DeleteButton />
        </>
       )}
      </div>
     );

    default:
     return null;
   }
  };

  // Load fonts used by text elements on demand (2D designer)
  useEffect(() => {
   const families = elements
    .filter((el) => el.type === "text")
    .map((el) => el.style.fontFamily)
    .filter((v): v is string => typeof v === "string" && v.trim().length > 0);

   if (families.length === 0) return;
   ensureFontsLoaded(families).catch(() => {
    // Non-fatal
   });
  }, [elements]);

  // Make canvas droppable
  const { setNodeRef: setDroppableRef, isOver } = useDroppable({
    id: "canvas-drop-zone",
  });

  // Combine refs
  const combinedRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
      }
      setDroppableRef(node);
    },
    [ref, setDroppableRef]
  );

  // Note: Zoom is applied via CSS transform, so we use baseDisplayWidth/Height/Scale
  // The transform scale handles the visual zoom

  // Prevent page scrolling when wheel is used over the canvas
  const handleWheelPrevent = (e: React.WheelEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };


  // Render background color and image to canvas
  useEffect(() => {
    if (!backgroundCanvasRef.current) {
      return;
    }

    const canvas = backgroundCanvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return;
    }

    // Set canvas size to match base display size
    canvas.width = baseDisplayWidth;
    canvas.height = baseDisplayHeight;

    // Always draw background color first
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // If we have a background image, load and draw it scaled to fit within canvas
    if (baseImage && imageDimensions) {
      const bgImg = new Image();
      bgImg.crossOrigin = "anonymous";
      bgImg.onload = () => {
        // Redraw background color first (in case image has transparency)
        ctx.fillStyle = backgroundColor;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        // Calculate scale to fit image within canvas while maintaining aspect ratio
        const canvasAspect = canvas.width / canvas.height;
        const imageAspect = bgImg.naturalWidth / bgImg.naturalHeight;
        
        let drawWidth: number;
        let drawHeight: number;
        let drawX = 0;
        let drawY = 0;
        
        if (imageAspect > canvasAspect) {
          // Image is wider - fit to width
          drawWidth = canvas.width;
          drawHeight = canvas.width / imageAspect;
          drawY = (canvas.height - drawHeight) / 2; // Center vertically
        } else {
          // Image is taller - fit to height
          drawHeight = canvas.height;
          drawWidth = canvas.height * imageAspect;
          drawX = (canvas.width - drawWidth) / 2; // Center horizontally
        }
        
        // Draw background image scaled to fit within canvas
        ctx.drawImage(bgImg, drawX, drawY, drawWidth, drawHeight);
      };
      bgImg.onerror = () => {
        // If image fails to load, just keep the background color
      };
      
      // Fetch background image via backend API to bypass CORS
      productCustomizationApi
        .fetchImageAsDataURL(baseImage)
        .then((dataURL) => {
          bgImg.src = dataURL;
        })
        .catch(() => {
          // Silently fail if fetch fails
        });
    }
  }, [baseImage, imageDimensions, backgroundColor, baseDisplayWidth, baseDisplayHeight]);


  return (
   <div
    ref={wrapperRef}
    style={{
      overflow: "auto",
      width: "100%",
      height: "100%",
      position: "relative",
    }}
   >
    <Box
     ref={combinedRef}
     className={`relative border-2 overflow-visible shadow-lg transition-colors ${
       isOver ? "border-blue-500" : selectedCanvas ? "border-blue-500" : "border-gray-300"
     } ${!isPreviewMode ? "cursor-pointer" : ""}`}
     style={{ 
       width: `${baseDisplayWidth}px`,
       height: `${baseDisplayHeight}px`,
       minWidth: `${baseDisplayWidth}px`,
       minHeight: `${baseDisplayHeight}px`,
       backgroundColor: backgroundColor,
       // Use logical size for positioning - elements are positioned relative to actual image size
       position: "relative",
       // Ensure no padding that could offset elements
       padding: "0",
       margin: "0",
       overflow: "hidden",
       touchAction: "none",
       transform: `scale(${zoomLevel})`,
       transformOrigin: "0 0",
     }}
     onClick={!isPreviewMode ? handleCanvasClick : undefined}
     onMouseUp={!isPreviewMode ? handleMouseUp : undefined}
     onMouseLeave={!isPreviewMode ? handleMouseUp : undefined}
     onWheel={handleWheelPrevent}
    >
    {/* Background canvas - draws background color and image */}
    <canvas
      ref={backgroundCanvasRef}
      className="absolute pointer-events-none"
      style={{
        top: "0px",
        left: "0px",
        width: `${baseDisplayWidth}px`,
        height: `${baseDisplayHeight}px`,
        zIndex: 0,
        userSelect: "none",
        pointerEvents: "none",
      }}
    />
    {elements.map((element) => renderElement(element))}
    {/* Remove Watermark button for admins */}
    {isAdmin && hasWatermark && onRemoveWatermark && (
      <button
        onClick={(e) => {
          e.stopPropagation();
          onRemoveWatermark();
        }}
        className="absolute top-2 right-2 z-50 bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-1.5 rounded-md shadow-lg transition-colors flex items-center gap-1.5"
        title="Remove watermark from background image"
        style={{
          fontSize: "11px",
          fontWeight: 500,
        }}
      >
        <span>Remove Watermark</span>
      </button>
    )}
    </Box>
   </div>
  );
 }
);

DesignCanvasComponent.displayName = "DesignCanvas";

export const DesignCanvas = DesignCanvasComponent;
export default DesignCanvasComponent;

